import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Image as ImageIcon, Plus, X, Upload, Trash2 } from "lucide-react";
import DashboardShell from "@/layouts/ProviderDashboardLayout";
import api from "@/lib/api";
import { useDocumentTitle } from "@/lib/useDocumentTitle";

async function upload(file, kind = "portfolio") {
  const fd = new FormData();
  fd.append("file", file);
  fd.append("kind", kind);
  const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
  return data;
}

const BLANK = { title: "", description: "", category_id: "", tags: [], media_ids: [], cover_media_id: null, sort_order: 0, public: true };

export default function PortfolioPage() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [previews, setPreviews] = useState({}); // media_id -> url cache
  useDocumentTitle("Portfolio");

  const load = async () => {
    const { data } = await api.get("/provider/portfolio");
    setItems(data || []);
  };
  useEffect(() => { load(); }, []);

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    for (const f of files) {
      try {
        const rec = await upload(f);
        setEditing((p) => ({ ...p, media_ids: [...(p.media_ids || []), rec.id], cover_media_id: p.cover_media_id || rec.id }));
        setPreviews((p) => ({ ...p, [rec.id]: rec.url }));
      } catch (err) { toast.error(`Upload failed: ${f.name}`); }
    }
  };

  const save = async () => {
    try {
      if (!editing.title) { toast.error("Title required"); return; }
      if (editing.id) await api.put(`/provider/portfolio/${editing.id}`, editing);
      else await api.post("/provider/portfolio", editing);
      toast.success("Saved");
      setEditing(null);
      load();
    } catch (err) { toast.error(err?.response?.data?.error || "Failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this portfolio item?")) return;
    try { await api.delete(`/provider/portfolio/${id}`); load(); toast.success("Deleted"); }
    catch (err) { toast.error("Failed"); }
  };

  return (
    <DashboardShell>
      <div className="max-w-5xl" data-testid="portfolio-page">
        <div className="flex items-start justify-between gap-3 mb-6 flex-wrap">
          <div>
            <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#165a71]">Provider · Portfolio</div>
            <h1 className="mt-2 font-display text-2xl md:text-3xl font-semibold text-[#071b22]">Showcase your best work.</h1>
          </div>
          <button onClick={() => setEditing({ ...BLANK })} className="inline-flex items-center gap-2 rounded-full bg-[#165a71] text-white px-4 py-2 text-sm font-medium" data-testid="portfolio-new">
            <Plus className="h-4 w-4" /> New item
          </button>
        </div>

        {items.length === 0 && !editing ? (
          <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center">
            <ImageIcon className="h-6 w-6 mx-auto text-[#165a71]" />
            <div className="mt-2 font-display text-lg text-[#071b22] font-semibold">Add your first portfolio piece.</div>
            <div className="text-sm text-[#252A34]/70 mt-1">Photos, before/afters, and case studies convert leads faster.</div>
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 md:grid-cols-3" data-testid="portfolio-grid">
            {items.map((it) => (
              <div key={it.id} className="rounded-2xl border border-slate-200 bg-white overflow-hidden group" data-testid={`portfolio-card-${it.id}`}>
                <div className="aspect-video bg-slate-100 flex items-center justify-center text-[#252A34]/40 text-xs">{it.media_ids?.length || 0} media</div>
                <div className="p-4">
                  <div className="font-display font-semibold text-[#071b22] truncate">{it.title}</div>
                  <div className="text-xs text-[#252A34]/60 mt-1 line-clamp-2">{it.description || "—"}</div>
                  <div className="mt-3 flex items-center gap-2">
                    <button onClick={() => setEditing({ ...it })} className="text-xs font-medium text-[#165a71]" data-testid={`portfolio-edit-${it.id}`}>Edit</button>
                    <button onClick={() => remove(it.id)} className="text-xs text-rose-600" data-testid={`portfolio-delete-${it.id}`}>Delete</button>
                    {!it.public && <span className="ml-auto text-[10px] uppercase tracking-wider text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">private</span>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {editing && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center bg-[#071b22]/50 p-4">
            <div className="w-full max-w-2xl rounded-3xl bg-white p-6 border border-slate-200" data-testid="portfolio-form">
              <div className="flex items-start justify-between">
                <div className="font-display text-lg font-semibold text-[#071b22]">{editing.id ? "Edit item" : "New portfolio item"}</div>
                <button onClick={() => setEditing(null)} className="rounded p-1 hover:bg-slate-100"><X className="h-4 w-4" /></button>
              </div>
              <div className="mt-4 space-y-3">
                <input value={editing.title} onChange={(e) => setEditing((p) => ({ ...p, title: e.target.value }))} placeholder="Title" className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" data-testid="portfolio-title" />
                <textarea rows={3} value={editing.description || ""} onChange={(e) => setEditing((p) => ({ ...p, description: e.target.value }))} placeholder="Describe the project…" className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" data-testid="portfolio-description" />
                <div className="flex items-center gap-2 flex-wrap">
                  <label className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs cursor-pointer" data-testid="portfolio-upload-btn">
                    <Upload className="h-3 w-3" /> Add photos
                    <input type="file" multiple className="hidden" accept="image/*" onChange={onUpload} />
                  </label>
                  <label className="inline-flex items-center gap-2 text-xs">
                    <input type="checkbox" checked={!!editing.public} onChange={(e) => setEditing((p) => ({ ...p, public: e.target.checked }))} data-testid="portfolio-public" /> Public
                  </label>
                  <div className="text-xs text-[#252A34]/60 ml-auto">{editing.media_ids?.length || 0} media</div>
                </div>
              </div>
              <div className="mt-4 flex justify-end gap-2">
                <button onClick={() => setEditing(null)} className="rounded-full border border-slate-200 px-4 py-2 text-sm">Cancel</button>
                <button onClick={save} className="rounded-full bg-[#165a71] text-white px-4 py-2 text-sm font-medium" data-testid="portfolio-save">Save</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardShell>
  );
}
